"use client";

import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { NodeIcon } from "@/components/ui/node-icon";
import { SkeletonReveal } from "@/components/ui/skeleton-reveal";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useActions, useTriggers, type ActionTriggerDefinition } from "@/hooks/useActionTriggerDefinitions";
import { useConnectorsStore } from "@/stores/connectorsStore";
import type { CUCostItem, CUCostsData } from "@/types/cu";
import { IconChevronDownOutline18, IconQuickSearchOutline18 } from "nucleo-ui-outline-18";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CostCardGridSkeleton } from "./BillingSkeletons";
import { CATEGORY_ORDER, getCategory, humanizeName } from "./cost-categories";
interface CostCardGridProps {
  data?: CUCostsData;
  isLoading?: boolean;
}
type FilterTab = "all" | "triggers" | "actions";
interface CostEntry {
  id: string;
  name: string;
  cost: number;
  kind: "Trigger" | "Action";
  category: string;
  definition?: ActionTriggerDefinition;
  connectorLogoUrl?: string;
}
const ITEMS_PER_CATEGORY = 50;
function getCostTierColor(cost: number): string {
  if (cost <= 2) return "text-green-500";
  if (cost <= 50) return "text-yellow-500";
  return "text-red-500";
}
export function CostCardGrid({
  data,
  isLoading
}: CostCardGridProps) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterTab>("all");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);
  const {
    triggers
  } = useTriggers();
  const {
    actions
  } = useActions();
  const connectorTypes = useConnectorsStore(state => state.connectorTypes);
  const loadConnectorTypes = useConnectorsStore(state_0 => state_0.loadConnectorTypes);
  useEffect(() => {
    if (connectorTypes.length === 0) {
      void loadConnectorTypes();
    }
  }, [connectorTypes.length, loadConnectorTypes]);
  const definitionsByType = useMemo(() => {
    const map = new Map<string, ActionTriggerDefinition>();
    [...triggers, ...actions].forEach(definition => map.set(definition.type, definition));
    return map;
  }, [triggers, actions]);
  const connectorLogoMap = useMemo(() => {
    const map_0 = new Map<string, string>();
    connectorTypes.forEach(connectorType => {
      if (connectorType.logoUrl) {
        map_0.set(connectorType.type, connectorType.logoUrl);
      }
    });
    return map_0;
  }, [connectorTypes]);

  // Merge triggers + all action categories into a flat list of CostEntry
  const allEntries = useMemo(() => {
    if (!data) return [];
    const entries: CostEntry[] = [];
    for (const t of data.triggers) {
      const definition_0 = definitionsByType.get(t.type);
      entries.push({
        id: t.type,
        name: humanizeName(t.type),
        cost: t.cost,
        kind: "Trigger",
        category: getCategory(t.type),
        definition: definition_0,
        connectorLogoUrl: definition_0?.connector?.type ? connectorLogoMap.get(definition_0.connector.type) : undefined
      });
    }
    const actionLists: CUCostItem[][] = [data.actions.dynamic, data.actions.builtIn, data.actions.logic];
    for (const list of actionLists) {
      for (const a of list) {
        const definition_1 = definitionsByType.get(a.type);
        entries.push({
          id: a.type,
          name: humanizeName(a.type),
          cost: a.cost,
          kind: "Action",
          category: getCategory(a.type),
          definition: definition_1,
          connectorLogoUrl: definition_1?.connector?.type ? connectorLogoMap.get(definition_1.connector.type) : undefined
        });
      }
    }
    return entries;
  }, [data, definitionsByType, connectorLogoMap]);

  // Filter + search
  const filtered = useMemo(() => {
    let result = allEntries;
    if (filter === "triggers") result = result.filter(e => e.kind === "Trigger");
    if (filter === "actions") result = result.filter(e_0 => e_0.kind === "Action");
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(e_1 => e_1.name.toLowerCase().includes(q) || e_1.id.toLowerCase().includes(q));
    }
    return result;
  }, [allEntries, filter, search]);

  // Group by category in defined order
  const grouped = useMemo(() => {
    const map_1 = new Map<string, CostEntry[]>();
    for (const entry of filtered) {
      const list_0 = map_1.get(entry.category) ?? [];
      list_0.push(entry);
      map_1.set(entry.category, list_0);
    }
    // Sort within each category by cost descending
    for (const list_1 of map_1.values()) {
      list_1.sort((a_0, b) => b.cost - a_0.cost);
    }
    // Return in defined order
    return CATEGORY_ORDER.filter(cat => map_1.has(cat)).map(cat_0 => ({
      category: cat_0,
      entries: map_1.get(cat_0)!
    }));
  }, [filtered]);
  const triggerCount = allEntries.filter(e_2 => e_2.kind === "Trigger").length;
  const actionCount = allEntries.filter(e_3 => e_3.kind === "Action").length;
  if (!data && !isLoading) {
    return <div className="flex h-48 items-center justify-center">
        <p className="body-sm">No cost data available</p>
      </div>;
  }
  return <SkeletonReveal loading={Boolean(isLoading)} skeleton={<CostCardGridSkeleton />}>
      {data && <div className="space-y-6">
          {/* Default Costs — preserved from original CostTable */}
          <div>
            <h4 className="body-sm-semibold mb-3">Default Costs</h4>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-ds-md shadow-ds-soft border-ds-border-default bg-ds-surface-card border px-4 py-3">
                <p className="label-xs">Trigger</p>
                <p className="tracking-display-sm text-ds-text-primary mt-1 text-lg font-semibold">
                  {data.defaults.trigger} CU
                </p>
              </div>
              <div className="rounded-ds-md shadow-ds-soft border-ds-border-default bg-ds-surface-card border px-4 py-3">
                <p className="label-xs">Built-in Action</p>
                <p className="tracking-display-sm text-ds-text-primary mt-1 text-lg font-semibold">
                  {data.defaults.builtIn} CU
                </p>
              </div>
              <div className="rounded-ds-md shadow-ds-soft border-ds-border-default bg-ds-surface-card border px-4 py-3">
                <p className="label-xs">Logic Action</p>
                <p className="tracking-display-sm text-ds-text-primary mt-1 text-lg font-semibold">
                  {data.defaults.logic} CU
                </p>
              </div>
              <div className="rounded-ds-md shadow-ds-soft border-ds-border-default bg-ds-surface-card border px-4 py-3">
                <p className="label-xs">Dynamic Action</p>
                <p className="tracking-display-sm text-ds-text-primary mt-1 text-lg font-semibold">
                  {data.defaults.dynamic} CU
                </p>
              </div>
            </div>
          </div>

          {/* Search + Filter */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <IconQuickSearchOutline18 className="text-ds-text-disabled absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input type="text" placeholder="Search triggers and actions..." value={search} onChange={e_4 => setSearch(e_4.target.value)} className="ds-soft-input border-0 pl-9 shadow-none focus-visible:ring-0" />
            </div>
            <Tabs value={filter} onValueChange={value => setFilter(value as FilterTab)} size="sm">
              <TabsList>
                <TabsTrigger value="all">All ({allEntries.length})</TabsTrigger>
                <TabsTrigger value="triggers">Triggers ({triggerCount})</TabsTrigger>
                <TabsTrigger value="actions">Actions ({actionCount})</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Categorized mini tables */}
          {grouped.length === 0 && <p className="body-sm py-8 text-center">No matching items</p>}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            {grouped.map(({
          category: category_0,
          entries: entries_0
        }) => {
          const isExpanded = expandedCategories.has(category_0);
          const hasMore = entries_0.length > ITEMS_PER_CATEGORY;
          const visibleEntries = isExpanded || !hasMore ? entries_0 : entries_0.slice(0, ITEMS_PER_CATEGORY);
          const hiddenCount = entries_0.length - ITEMS_PER_CATEGORY;
          return <div key={category_0} className="rounded-ds-14 shadow-ds-soft border-ds-border-default bg-ds-surface-card overflow-hidden border">
                  <div className="border-ds-border-default border-b bg-zinc-50/80 px-4 py-3 dark:bg-zinc-950">
                    <h4 className="micro-label font-semibold">{category_0}</h4>
                  </div>
                  <div className="divide-ds-neutral-100 dark:divide-ds-neutral-800 divide-y">
                    {visibleEntries.map(entry_0 => <div key={entry_0.id} className="flex items-center justify-between gap-3 px-4 py-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 overflow-hidden">
                            <NodeIcon id={entry_0.id} subject={entry_0.definition?.name || entry_0.name} context={entry_0.definition?.description || `${entry_0.kind} in ${entry_0.category}`} logoUrl={entry_0.definition?.logoUrl} connector={entry_0.definition?.connector?.type ? {
                      type: entry_0.definition.connector.type,
                      logoUrl: entry_0.connectorLogoUrl
                    } : undefined} className="h-5 w-5 shrink-0" alt={entry_0.name} color="#2450FF" />
                            <p className="body-sm-medium truncate">{entry_0.name}</p>
                            <span className="dark:text-ds-text-subtle shrink-0 text-zinc-300">·</span>
                            <Badge variant="surface" className="text-2xs h-5 whitespace-nowrap px-1.5 py-0 font-medium">
                              {entry_0.kind}
                            </Badge>
                          </div>
                        </div>
                        <span className={`leading-label shrink-0 text-xs font-semibold ${getCostTierColor(entry_0.cost)}`}>
                          {entry_0.cost}
                        </span>
                      </div>)}
                    {hasMore && <button type="button" onClick={() => toggleCategory(category_0)} className="text-ds-text-muted hover:text-ds-text-secondary dark:hover:bg-ds-neutral-800 hover:bg-ds-surface-hover flex w-full items-center justify-center gap-1 px-3 py-2.5 text-xs font-medium transition-colors dark:hover:text-zinc-300">
                        {isExpanded ? "Show less" : `Show all ${entries_0.length} items (+${hiddenCount} more)`}
                        <IconChevronDownOutline18 className={`h-3.5 w-3.5 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                      </button>}
                  </div>
                </div>;
        })}
          </div>

          {/* Cost tier legend */}
          <div className="border-ds-border-default text-ds-text-muted flex gap-4 border-t pt-3 text-xs">
            <span>
              <span className="text-green-500">●</span> Low (≤2 CU)
            </span>
            <span>
              <span className="text-yellow-500">●</span> Medium (3–50 CU)
            </span>
            <span>
              <span className="text-red-500">●</span> High (50+ CU)
            </span>
          </div>
        </div>}
    </SkeletonReveal>;
}